/**
 * Shared JSON Schema definitions. The server derives its route types from
 * these; the client derives its response types from the same objects. There is
 * no second, hand-maintained copy of any of this.
 */
import { Type } from '@sinclair/typebox';
import { CURRENCIES } from './money.js';
/**
 * None of the schemas in this file carry a `$id`.
 *
 * They are *inlined* into route schemas, often several times in one response —
 * the reconciliation report alone embeds the money envelope five times. A
 * repeated `$id` within a single schema is an ambiguous reference, and
 * fast-json-stringify refuses to build the serializer: the API fails at boot
 * with "reference Money resolves to more than one schema".
 *
 * If a shared, by-reference schema is ever wanted, register it once with
 * `app.addSchema()` and use `Type.Ref()` at the call sites — do not put the id
 * back here.
 */
export const CurrencySchema = Type.Union(CURRENCIES.map((c) => Type.Literal(c)), { description: 'ISO 4217 code. v1 is USD-only.' });
/**
 * The money envelope required by §7 of the brief: every money-bearing field in
 * every response is this shape. Never a bare number, never a formatted string.
 *
 * `amountCents` is a JSON integer, not a float and not a decimal string. It is
 * converted to `bigint` at both boundaries by the codec in ./money.ts.
 */
export const MoneySchema = Type.Object({
    amountCents: Type.Integer({
        minimum: Number.MIN_SAFE_INTEGER,
        maximum: Number.MAX_SAFE_INTEGER,
        description: 'Integer minor units. Negative values are permitted (clawbacks).',
    }),
    currency: CurrencySchema,
}, { additionalProperties: false });
/** Lifecycle states an earning can be in. Mirrors §4.3 of the brief. */
export const EARNING_STATES = [
    'pending',
    'available',
    /**
     * A review is open and the money has been moved out of available.
     *
     * Not terminal, and deliberately not `reversed`: nothing has been decided.
     * The creator is still owed it, and the state exists so a payout can be
     * refused with "this is under review" rather than with an arithmetic result.
     */
    'held',
    'payout_requested',
    'payout_settled',
    'reversed',
    'clawed_back',
    /**
     * The authorisation was never cleared and the hold dropped off.
     *
     * Deliberately distinct from `reversed`, even though the ledger effect is the
     * same reversal. "The merchant refunded you" and "the payment never
     * completed" are different answers to a fan asking where their cashback went,
     * and the *rate* of these is an integration health signal — a sudden rise
     * means clearings have stopped arriving, which no refund metric would show.
     */
    'expired',
];
export const EarningStateSchema = Type.Union(EARNING_STATES.map((s) => Type.Literal(s)), {});
/** Money plus the ledger state it is held in — the §7 response shape. */
export const StatedMoneySchema = Type.Object({
    amountCents: Type.Integer(),
    currency: CurrencySchema,
    state: EarningStateSchema,
}, { additionalProperties: false });
/** RFC 7807 problem+json. Every error response in the API is this shape. */
export const ProblemSchema = Type.Object({
    type: Type.String({ format: 'uri-reference' }),
    title: Type.String(),
    status: Type.Integer({ minimum: 100, maximum: 599 }),
    detail: Type.Optional(Type.String()),
    instance: Type.Optional(Type.String()),
    requestId: Type.Optional(Type.String()),
    errors: Type.Optional(Type.Array(Type.Object({
        path: Type.String(),
        message: Type.String(),
    }))),
}, { additionalProperties: true });
/** Opaque, stable cursor pagination. Offsets are never exposed. */
export const PageQuerySchema = Type.Object({
    cursor: Type.Optional(Type.String({ maxLength: 512 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 25 })),
});
export function PagedSchema(item) {
    return Type.Object({
        data: Type.Array(item),
        nextCursor: Type.Union([Type.String(), Type.Null()]),
    });
}
/**
 * Chart of accounts (§4.2). Typed accounts, never a `users.balance` column.
 *
 * `platform_cash` is an addition to the brief's list. It represents money
 * actually held at the bank, and without it no event crossing the system
 * boundary can balance. See docs/adr/0006-ledger-postings.md.
 */
export const ACCOUNT_TYPES = [
    'platform_cash',
    'merchant_escrow',
    'merchant_receivable',
    'creator_pending',
    'creator_available',
    /**
     * Matured money a review has frozen. Held rather than reversed because the
     * review has not decided anything yet — the creator is still owed it, and
     * moving it here is what stops it being paid out while someone looks.
     */
    'creator_held',
    'creator_paid',
    'member_pending',
    'member_available',
    'member_held',
    'member_paid',
    'platform_revenue',
    'platform_fees_payable',
    'reserve_clawback',
];
/**
 * Normal balance direction per account type, in the accounting sense.
 * Asset/expense accounts are debit-normal; liability/equity/revenue accounts
 * are credit-normal. The ledger stores signed amounts, so this table is what
 * lets the reconciliation report say "this account is upside down".
 */
export const ACCOUNT_NORMAL_BALANCE = {
    platform_cash: 'debit',
    merchant_escrow: 'credit',
    merchant_receivable: 'debit',
    creator_pending: 'credit',
    creator_available: 'credit',
    // Clearing accounts, credit-normal: while money sits here it is committed to
    // a payout but not yet settled, so it is still an obligation we owe. Treating
    // them as debit-normal would render "committed to payout" as a negative
    // number on a creator's dashboard.
    creator_paid: 'credit',
    // Held money is still owed; it has only been stopped. Debit-normal here would
    // show a creator under review a negative frozen balance.
    creator_held: 'credit',
    member_pending: 'credit',
    member_available: 'credit',
    member_held: 'credit',
    member_paid: 'credit',
    platform_revenue: 'credit',
    platform_fees_payable: 'credit',
    reserve_clawback: 'credit',
};
/** Business facts that move money. One per row in ledger_events. */
export const LEDGER_EVENT_TYPES = [
    'merchant_escrow_funded',
    'conversion_attributed',
    /** The cleared amount differed from the authorised amount; this is the delta. */
    'conversion_adjusted',
    'earning_matured',
    /** A review froze a matured earning: available -> held. */
    'earning_held',
    /** The review cleared it: held -> available. */
    'earning_released',
    'payout_requested',
    'payout_settled',
    /** A fan withdrawing their cashback. The member-side twin of a payout. */
    'redemption_requested',
    'redemption_settled',
    'conversion_reversed',
    /**
     * Cashback in a deleted or abandoned account passing to the platform.
     *
     * Distinct from `conversion_reversed` on purpose: a reversal means the sale
     * came back and the merchant is owed their commission again, and forfeited
     * money reported as a reversal would misstate both the member's history and
     * the platform's revenue.
     */
    'balance_forfeited',
    'clawback_recovered',
    'correction',
];
export const LEDGER_OWNER_KINDS = ['member', 'creator', 'merchant', 'platform'];
/** Which kind of owner each account type belongs to. Mirrors the DB CHECK. */
export const ACCOUNT_OWNER_KIND = {
    platform_cash: 'platform',
    merchant_escrow: 'merchant',
    merchant_receivable: 'merchant',
    creator_pending: 'creator',
    creator_available: 'creator',
    creator_held: 'creator',
    creator_paid: 'creator',
    member_pending: 'member',
    member_available: 'member',
    member_held: 'member',
    member_paid: 'member',
    platform_revenue: 'platform',
    platform_fees_payable: 'platform',
    reserve_clawback: 'platform',
};
export const PRINCIPAL_KINDS = ['member', 'creator', 'merchant', 'admin'];
export const HeadersSchema = Type.Object({
    'idempotency-key': Type.Optional(Type.String({ minLength: 8, maxLength: 255 })),
    'x-request-id': Type.Optional(Type.String({ maxLength: 255 })),
});
/* ------------------------------------------------------------------ *
 * Member response shapes
 *
 * These moved here from `apps/api/src/routes/`, where they were the
 * server's private business and each client kept its own hand-written
 * copy of what it believed the server sent.
 *
 * `apps/mobile` believed wrong. Its eligibility interface had six fields
 * and shared exactly one name with this schema, so `elig.reasons.length`
 * read `.length` off `undefined` and took the wallet screen down on
 * launch; its activity interface read `.items` from a response whose
 * array is called `data`, which never threw and simply showed an empty
 * list forever. Both typechecked, because `request<T>()` casts the JSON
 * and nothing compared the two ends.
 *
 * Living here, they are one definition: the route derives its response
 * validation from these, and both clients derive their types from them
 * through `./wire.js`. A field renamed on the server is a compile error
 * in the app rather than a crash on a phone.
 * ------------------------------------------------------------------ */
/** Ledger-derived balances, for a member or a creator. */
export const BalanceSummarySchema = Type.Object({
    pending: MoneySchema,
    available: MoneySchema,
    inFlight: MoneySchema,
    lifetimeEarned: MoneySchema,
});
/**
 * One page of ledger activity.
 *
 * The array is `data`, and the rows are ledger movements — an event and an
 * account, not a shop and a purchase. A client wanting merchant names has to
 * follow `conversionId`; there is no merchant on this row and never was.
 */
export const ActivitySchema = Type.Object({
    data: Type.Array(Type.Object({
        id: Type.String(),
        occurredAt: Type.String(),
        eventType: Type.String(),
        accountType: Type.Union(ACCOUNT_TYPES.map((t) => Type.Literal(t))),
        amount: MoneySchema,
        // Null where no single purchase explains the movement — a payout, a
        // redemption, a correction. The client offers "why?" only where it is set.
        conversionId: Type.Union([Type.String(), Type.Null()]),
    })),
    nextCursor: Type.Union([Type.String(), Type.Null()]),
});
/**
 * Why a cash out can or cannot happen, and what would change it.
 *
 * There is no `eligible` boolean: `blockedReason === null` is the whole test,
 * and a separate flag is a second source of truth that can disagree with the
 * reason beside it. There is no `reasons: string[]` either — the reason is a
 * closed union so the client owns the wording, and a server sending display
 * copy is how two clients end up saying different things about the same state.
 */
export const REDEMPTION_BLOCKED_REASONS = [
    'terms_acceptance_required',
    'negative_balance',
    'below_minimum',
    'funds_on_hold',
    'redemption_in_flight',
];
export const RedemptionEligibilitySchema = Type.Object({
    available: MoneySchema,
    /** What could leave right now. `available` minus anything still held. */
    redeemable: MoneySchema,
    onHold: MoneySchema,
    blockedReason: Type.Union([
        ...REDEMPTION_BLOCKED_REASONS.map((r) => Type.Literal(r)),
        Type.Null(),
    ]),
    termsToAccept: Type.Union([
        Type.Object({ version: Type.String(), effectiveAt: Type.String() }),
        Type.Null(),
    ]),
    policy: Type.Object({
        version: Type.Integer(),
        minimumRedemption: MoneySchema,
        holdDays: Type.Integer(),
    }),
});
/**
 * One merchant in the shop catalog.
 *
 * Moved here from `apps/api/src/routes/catalog.ts` when the storefront was
 * built, and it was the last hand-written response interface in
 * `apps/mobile` — the one the earlier contract fix recorded as "the next one to
 * move". Two clients render this now and both derive from here.
 *
 * `cashbackBps` and `cashbackOnHundred` are null only for a caller who is not a
 * member. Every member holds the house connection from account creation, so a
 * fan browsing this always has a rate.
 */
export const OfferSchema = Type.Object({
    id: Type.String(),
    merchantId: Type.String(),
    merchantName: Type.String(),
    merchantSlug: Type.String(),
    offerName: Type.String(),
    /** What the merchant pays on a sale. Basis points of the purchase. */
    commissionBps: Type.Integer(),
    /** Which network pays it — where a member must be routed to actually earn. */
    provider: Type.String(),
    cashbackBps: Type.Union([Type.Integer(), Type.Null()]),
    creatorDisplayName: Type.Union([Type.String(), Type.Null()]),
    /** The same figure as money, on a $100 purchase. Basis points persuade nobody. */
    cashbackOnHundred: Type.Union([MoneySchema, Type.Null()]),
    /**
     * The merchant's mark, or null.
     *
     * Null for every merchant today: migration 0029 added the column and nothing
     * fills it, because no network adapter lists a catalog. Clients render a
     * monogram tile in its place, at the size a logo will take — see
     * `./brand.js`. When a sync lands, the tiles become logos and nothing else
     * changes.
     */
    logoUrl: Type.Union([Type.String(), Type.Null()]),
});
//# sourceMappingURL=schemas.js.map